import {Button, Card, Col, Row, Table, Tag} from "antd"
import {useDispatch, useSelector} from "react-redux"
import {StateType} from "../../store/store"
import React, {useCallback, useEffect, useMemo} from "react"
import {Loading} from "../../components/Loading/Loading"
import {getObjectGroups, setObjectGroups, setObjectGroupsFilter, setWSObjectGroup} from "../../store/objectGroupReducer"
import {isActiveOptionsSelect, limitOptionsSelect} from "../../helps/optionsSelect"
import {useHistory, useLocation} from "react-router-dom"
import {filterFromString} from "../../helps/filterFromString"
import deepEqual from "deep-equal"
import {filterToString} from "../../helps/filterToString"
import {center} from "../../helps/tableHelper"
import {Helmet} from "react-helmet-async"
import {FormSelect} from "../../components/Form/FormSelect"
import {ID_SYSTEM_COMPANY} from "../../constants/constants"
import {useGetCompanies} from "../../hooks/useFetchHooks"
import {FormInput} from "../../components/Form/FormInput"

export const ObjectGroups = () => {
    const dispatch = useDispatch()
    const location = useLocation()
    const history = useHistory()
    const profile = useSelector((state: StateType) => state.appReducer.profile)
    const objectGroups = useSelector((state: StateType) => state.objectGroupReducer.objectGroups)
    const filter = useSelector((state: StateType) => state.objectGroupReducer.filter)
    const ws = useSelector((state: StateType) => state.objectGroupReducer.ws)
    const controller = useMemo(() => new AbortController(), [])

    const getCompanyParams = useMemo(() => ({excludeSystem: true}), [])
    const companies = useGetCompanies(getCompanyParams)

    const columns = useMemo(() => {
        const cols = []

        if (profile?.idCompany === ID_SYSTEM_COMPANY) cols.push({
            title: 'Компания',
            dataIndex: 'nameCompany',
            key: 'nameCompany',
            align: center,
        })

        cols.push(
            {
                title: 'Название',
                dataIndex: 'name',
                key: 'name',
                align: center,
            },
            {
                title: 'Часовой пояс',
                dataIndex: 'timeOffsetName',
                key: 'timeOffsetName',
                align: center,
            },
            {
                title: 'Статус',
                dataIndex: 'active',
                key: 'active',
                render: (active: boolean) => {
                    return (
                        <Tag color={active ? 'green' : 'volcano'} style={{marginRight: 0}}>{active ? 'Активно' : 'Не активно'}</Tag>
                    )
                },
                align: center,
            },
        )

        return cols
    }, [profile])

    useEffect(() => {
        const filterString = filterFromString(location.search)
        if (filterString && !deepEqual(filterString, filter)) {
            dispatch(setObjectGroupsFilter({...filterString}))
        } else if (location.search !== filterToString(filter)) {
            history.replace({search: filterToString(filter)})
        }
    }, [dispatch, filter, history, location])

    const getObjectGroupsFunc = useCallback((withoutLoadingIndicator = false) => {
        if (location?.search) {
            dispatch(getObjectGroups({
                ...filterFromString(location.search),
                withoutLoadingIndicator,
                controller,
            }))
        }
    }, [dispatch, location, controller])

    useEffect(() => {
        getObjectGroupsFunc()

        return () => {
            dispatch(setObjectGroups(null))
        }
    }, [dispatch, getObjectGroupsFunc])

    useEffect(() => {
        return () => {
            controller.abort()
        }
    }, [dispatch, controller])

    useEffect(() => {
        if (ws) {
            getObjectGroupsFunc(true)
        }
        dispatch(setWSObjectGroup(null))
    }, [dispatch, ws, getObjectGroupsFunc])

    const data = useMemo(() => objectGroups?.data?.map(i => {
        return {
            key: i.id,
            id: i.id,
            nameCompany: i.nameCompany,
            name: i.name,
            active: i.active,
            deleted: i.deleted,
            timeOffsetName: i.timeOffsetName,
        }
    }), [objectGroups])

    const companyOptions = useMemo(() => companies?.data?.map(i => ({label: i.name, value: i.id})), [companies])

    return (
        <div className="tabled">
            <Helmet>
                <title>Группы объектов</title>
            </Helmet>
            <Row gutter={[24, 0]}>
                <Col xs={24}>
                    <Card variant="borderless"
                          className="criclebox tablespace mb-24"
                          title={
                              <div className='table-title'>
                                  <div>Группы объектов</div>
                                  <Button onClick={() => history.push('/objectGroupForm')} type="link" size="middle" style={{marginTop: 5}}>
                                      Добавить
                                  </Button>
                              </div>
                          }
                          extra={
                              <div className='table-title-extra'>
                                  <FormInput placeholder="Поиск"
                                             containerStyle={{marginBottom: 0, width: "auto", minWidth: 100}}
                                             value={filter?.search}
                                             name="search"
                                             onChange={(e) => history.replace({search: filterToString({...filter, search: e.target.value, offset: 0})})}
                                             allowClear
                                  />

                                  {
                                      profile?.idCompany === ID_SYSTEM_COMPANY
                                          ?
                                          <FormSelect value={filter?.idCompany}
                                                      containerStyle={{marginBottom: 0, width: 'auto', minWidth: 100}}
                                                      dropdownStyle={{minWidth: 'min-content'}}
                                                      onChange={(value) => history.replace({search: filterToString({...filter, idCompany: value, offset: 0})})}
                                                      options={companyOptions}
                                                      placeholder="Компания"
                                                      allowClear
                                                      placement="bottomRight"
                                          />
                                          : null
                                  }

                                  <FormSelect value={filter?.status}
                                              containerStyle={{marginBottom: 0, width: 'auto', minWidth: 100}}
                                              dropdownStyle={{minWidth: 'min-content'}}
                                              onChange={(value) => history.replace({search: filterToString({...filter, status: value, offset: 0})})}
                                              options={isActiveOptionsSelect}
                                              placeholder="Статус"
                                              allowClear
                                              placement="bottomRight"
                                  />

                                  <FormSelect value={filter?.limit}
                                              containerStyle={{marginBottom: 0, width: 'auto'}}
                                              dropdownStyle={{minWidth: 'min-content'}}
                                              onChange={(value) => history.replace({search: filterToString({...filter, limit: value, offset: 0})})}
                                              options={limitOptionsSelect}
                                              placement="bottomRight"
                                  />
                              </div>
                          }
                    >
                        <div className="table-responsive">
                            {
                                objectGroups
                                    ? <Table columns={columns}
                                             dataSource={data}
                                             pagination={filter?.limit ? {
                                                 position: objectGroups?.data?.length >= 10 ? ['bottomLeft', 'topLeft'] : ['bottomLeft'],
                                                 pageSize: filter.limit,
                                                 hideOnSinglePage: true,
                                                 total: objectGroups.total,
                                                 current: filter?.offset / filter?.limit + 1,
                                                 onChange: (page, pageSize) => {
                                                     const obj = {
                                                         limit: pageSize,
                                                         offset: (page - 1) * pageSize,
                                                     }
                                                     history.replace({search: filterToString({...filter, ...obj})})
                                                 },
                                                 showSizeChanger: false,
                                             } : false}
                                             className="ant-border-space"
                                             onRow={({id}) => ({
                                                 onClick: () => history.push(`/objectGroup/${id}`),
                                                 onMouseDown: (e) => e.button === 1 && window.open(`/objectGroup/${id}`, '_blank', 'noopener,noreferrer')
                                             })}
                                             rowClassName={({deleted}) => deleted ? 'table-row-deleted' : ''}
                                    />
                                    : <Loading/>
                            }
                        </div>
                    </Card>
                </Col>
            </Row>
        </div>
    )
}
